import { FC, InputHTMLAttributes, useEffect, useMemo, useCallback, useState } from 'react'
import styles from 'components/features/pfm/InputRangeNative/InputRangeNative.styl'
import cn from 'classnames'
import { Omit } from 'interfaces'

type InputRangeNativeProps =
  & Omit<InputHTMLAttributes<HTMLInputElement>, 'min' | 'max' | 'value'>
  & {
    min: number
    max: number
    value: number
    theme?: 'regular' | 'small' | 'big' | 'bigThumbOnly'
    innerRef?: (ref: HTMLInputElement | null) => void
  }

export const InputRangeNative: FC<InputRangeNativeProps> = (props) => {
  const { className, value, max, min, innerRef, theme = 'regular', ...restProps } = props

  const [inputNode, setInputNode] = useState<HTMLInputElement | null>(null)
  const handleInputRef = useCallback((ref: HTMLInputElement | null) => {
    setInputNode(ref)
    innerRef && innerRef(ref)
  }, [innerRef])

  useEffect(() => {
    if (!inputNode) {
      return
    }

    const stop = (event: Event) => {
      event.stopPropagation()
    }

    inputNode.addEventListener('touchstart', stop, false)
    inputNode.addEventListener('touchmove', stop, false)

    return () => {
      inputNode.removeEventListener('touchstart', stop, false)
      inputNode.removeEventListener('touchmove', stop, false)
    }
  }, [inputNode])

  const style = useMemo(() => {
    const v = value - min
    const d = max - min
    const progress = d > 0 ? v / d * 100 : 0
    return {
      '--progress': `${ progress || 0 }%`,
    } as {}
  }, [value, max, min])

  return (
    <input
      type='range'
      className={ cn(styles.input, styles[theme], className) }
      style={ style }
      value={ value }
      max={ max }
      min={ min }
      ref={ handleInputRef }
      { ...restProps }
    />
  )
}
